/**
 * Shared slug utilities for netfair entities (Company, CMSProduct, CMSNews,
 * Lecture, CMSJobListing, Event).
 *
 * ES-module mirror for the React frontend. scripts/slug/slugify.js is the
 * single source of truth: keep the logic here byte-identical to it (the test
 * scripts/slug-frontend/slugify-mirror.test.js enforces this) and change
 * behaviour only there.
 *
 * Two flavours exist:
 *   slugify()    - canonical slug for NEW entities: every non-alphanumeric run
 *                  becomes a single hyphen, German umlauts are transliterated
 *                  (ae/oe/ue/ss), diacritics are stripped.
 *   wpSlugify()  - reproduces WordPress' sanitize_title_with_dashes() closely
 *                  enough to match legacy slugs by name (used by the backfill
 *                  only). WordPress deletes characters like "&", "+" or "/"
 *                  instead of turning them into hyphens and turns "." into "-".
 */
/* eslint-disable no-useless-escape */

export const DEFAULT_MAX_LENGTH = 120;
export const MAX_SLUG_LENGTH = 200;
const DEFAULT_MAX_SUFFIX_ATTEMPTS = 200;
export const PREFERRED_LANGUAGES = ['de', 'en'];

const GERMAN_MAP = {
  'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss',
  'æ': 'ae', 'ø': 'oe', 'œ': 'oe', 'đ': 'd', 'ł': 'l',
};

const HYPHEN_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function safeDecode(text) {
  try {
    return decodeURIComponent(text);
  } catch (e) {
    return text;
  }
}

/**
 * Lowercases and removes accents. With `german: true` umlauts become
 * ae/oe/ue/ss (the WordPress de_DE behaviour and our canonical choice);
 * with `german: false` they simply lose their diaeresis (ä -> a), which is
 * what older WordPress content on the site used.
 */
export function transliterate(text, german = true) {
  let s = String(text).toLowerCase();
  if (german) {
    s = s.replace(/[äöüßæøœđł]/g, (ch) => GERMAN_MAP[ch] || ch);
  } else {
    s = s.replace(/ß/g, 'ss');
  }
  // NFKD also folds compatibility characters such as "²" -> "2" or "₂" -> "2".
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

function truncateAtWord(slug, maxLength) {
  if (!maxLength || slug.length <= maxLength) return slug;
  const cut = slug.slice(0, maxLength);
  const lastDash = cut.lastIndexOf('-');
  return (lastDash > 0 ? cut.slice(0, lastDash) : cut).replace(/-+$/g, '');
}

/**
 * Canonical slug for new entities.
 * @param {string} text
 * @param {{maxLength?: number, german?: boolean}} [options]
 * @returns {string} '' when nothing usable is left
 */
export function slugify(text, options = {}) {
  if (text === null || text === undefined) return '';
  const maxLength = options.maxLength === undefined ? DEFAULT_MAX_LENGTH : options.maxLength;
  const german = options.german === undefined ? true : options.german;
  let s = safeDecode(String(text));
  s = s.replace(/<[^>]*>/g, ' ');
  s = transliterate(s, german);
  s = s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return truncateAtWord(s, maxLength);
}

/**
 * WordPress-compatible slug (sanitize_title_with_dashes), used to match
 * legacy URLs by entity name. Returns '' when nothing usable is left.
 * @param {string} text
 * @param {{german?: boolean}} [options]
 */
export function wpSlugify(text, options = {}) {
  if (text === null || text === undefined) return '';
  const german = options.german === undefined ? true : options.german;
  let s = safeDecode(String(text));
  s = s.replace(/<[^>]*>/g, '');
  s = transliterate(s, german);
  s = s.replace(/&nbsp;|&#160;| | | /g, ' ');
  s = s.replace(/&amp;/g, '&');
  s = s.replace(/[‘’‚‛'`]/g, '');
  s = s.replace(/\./g, '-');
  s = s.replace(/[^a-z0-9 _-]/g, '');
  s = s.replace(/\s+/g, '-');
  s = s.replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  return s;
}

/**
 * All plausible legacy slug spellings for a display name, most likely first.
 * Deduplicated, empty strings removed.
 */
export function slugCandidates(text) {
  const out = [];
  for (const candidate of [
    wpSlugify(text, { german: true }),
    wpSlugify(text, { german: false }),
    slugify(text, { german: true, maxLength: 0 }),
    slugify(text, { german: false, maxLength: 0 }),
  ]) {
    if (candidate && !out.includes(candidate)) out.push(candidate);
  }
  return out;
}

/**
 * Normalizes an admin-entered slug: decodes, lowercases, transliterates and
 * enforces the canonical charset. Returns '' for unusable input.
 */
export function normalizeSlug(input) {
  return slugify(input, { maxLength: MAX_SLUG_LENGTH });
}

/** Strict canonical form: lowercase ascii words joined by single hyphens. */
export function isCanonicalSlug(slug) {
  return typeof slug === 'string' && slug.length > 0 && slug.length <= MAX_SLUG_LENGTH && HYPHEN_SLUG_RE.test(slug);
}

/**
 * Lenient validity used for legacy WordPress slugs, which may contain
 * unicode (the old site kept characters like "²" or "·", percent-encoded).
 * No whitespace, no slashes, no uppercase, no leading/trailing hyphen.
 */
export function isValidSlug(slug) {
  if (typeof slug !== 'string' || slug.length === 0 || slug.length > MAX_SLUG_LENGTH) return false;
  if (/[\s\/\\?#%]/.test(slug)) return false;
  if (slug !== slug.toLowerCase()) return false;
  if (slug.startsWith('-') || slug.endsWith('-')) return false;
  return true;
}

export function withSuffix(base, n) {
  return `${base}-${n}`;
}

/** "steris-gmbh-3" -> "steris-gmbh"; slugs without numeric suffix are returned unchanged. */
export function stripNumericSuffix(slug) {
  return String(slug).replace(/-\d+$/, '');
}

/**
 * Extracts the text to build a slug from. Accepts a plain string or a
 * MultiLangString list ([{language|lang, text}]). Preferred languages first,
 * then the first non-empty entry.
 */
export function pickSourceText(value, preferredLanguages = PREFERRED_LANGUAGES) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (!Array.isArray(value)) return '';
  const entries = value
    .filter((e) => e && typeof e === 'object')
    .map((e) => ({ language: e.language || e.lang || '', text: typeof e.text === 'string' ? e.text.trim() : '' }))
    .filter((e) => e.text);
  for (const lang of preferredLanguages) {
    const hit = entries.find((e) => e.language === lang);
    if (hit) return hit.text;
  }
  return entries.length ? entries[0].text : '';
}

/**
 * Finds a free slug: `base`, then `base-2`, `base-3`, ...
 * @param {string} base
 * @param {(slug: string) => Promise<boolean>|boolean} isTaken
 * @param {{maxAttempts?: number, startAt?: number}} [options]
 * @returns {Promise<string>} '' when base is empty
 */
export async function makeUniqueSlug(base, isTaken, options = {}) {
  if (!base) return '';
  const maxAttempts = options.maxAttempts || DEFAULT_MAX_SUFFIX_ATTEMPTS;
  const startAt = options.startAt || 2;
  if (!(await isTaken(base))) return base;
  for (let n = startAt; n < startAt + maxAttempts; n += 1) {
    const candidate = withSuffix(base, n);
    if (!(await isTaken(candidate))) return candidate;
  }
  throw new Error(`Could not find a free slug for "${base}" after ${maxAttempts} attempts`);
}

